import React, {
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useCallback,
} from "react";
import { Container, Form, Row, Col } from "react-bootstrap";
import PageLoading from "components/PageLoading";
import { IMaskInput } from "react-imask";
import SaveSubmitButtons from "../../../../../components/SaveSubmitButtons/SaveSubmitButtons";
import type { DadosSindicatoDetalhe } from "../../../../../services";
import {
  patchAvaliacaoSolicitacao,
  type PatchAvaliacaoSolicitacaoPayload,
} from "../../../../../services/avaliacaoSolicitacaoService";
import "../../V1/Tabs/CadastroTab/CadastroTab.css";

export interface CadastroTabV2Props {
  isActive?: boolean;
  selectedYear?: number;
  solicitacaoId?: number | null;
  isLoadingDados?: boolean;
  sindicatoDados?: DadosSindicatoDetalhe;
  
  readOnly?: boolean;
  onSaveAll?: () => Promise<
    { show?: boolean; message?: string; type?: string; reload?: boolean } | void
  >;
  onProximo?: () => void;
  submitLabel?: string;
  submitDisabled?: boolean;
  onEnviarPreConfirmacao?: () => Promise<
    { show?: boolean; message?: string; type?: string } | void
  >;
}

export interface CadastroTabV2RefHandle {
  handleSave?: () => Promise<{ show?: boolean; message?: string; type?: string }>;
  isDirty?: () => boolean;
  buildPatchPayload?: () => PatchAvaliacaoSolicitacaoPayload | null;
  markAsSaved?: () => void;
}

interface FormState {
  nome: string;
  cnpj: string;
  sigla: string;
  regional: string;
  ordenacao: string;
  grupo: string;
  presidente: string;
  email: string;
  telefone: string;
  coordenador: string;
  telCoordenador: string;
  responsavel: string;
  cargo: string;
}

const CadastroTabV2 = forwardRef<CadastroTabV2RefHandle, CadastroTabV2Props>(
  (
    {
      isActive,
      isLoadingDados = false,
      sindicatoDados,
      solicitacaoId,
      readOnly = false,
      onSaveAll,
      onProximo,
      submitLabel = "PRÓXIMO",
      submitDisabled = false,
      onEnviarPreConfirmacao,
    },
    ref,
  ) => {
    const [formData, setFormData] = useState<FormState>({
      nome: "",
      cnpj: "",
      sigla: "",
      regional: "",
      ordenacao: "",
      grupo: "",
      presidente: "",
      email: "",
      telefone: "",
      coordenador: "",
      telCoordenador: "",
      responsavel: "",
      cargo: "",
    });

    const [loading, setLoading] = useState(true);
    const [errors, setErrors] = useState<Record<string, boolean>>({});

    const initialEditableRef = React.useRef<{
      telefone: string;
      coordenador: string;
      telCoordenador: string;
      responsavel: string;
      cargo: string;
    } | null>(null);

    useEffect(() => {
      if (isLoadingDados) {
        // Só tela cheia de loading na primeira carga. Refetch após salvar não pode
        // esconder o formulário — desmonta os botões e o overlay de sucesso some.
        if (!sindicatoDados) {
          setLoading(true);
        }
        return;
      }

      if (!sindicatoDados) {
        setLoading(false);
        return;
      }

      const telefone = sindicatoDados.telefoneSindicato || "";
      const coordenador =
        sindicatoDados.nomeCoordenador || sindicatoDados.nomeCordenador || "";
      const telCoordenador =
        sindicatoDados.telefoneCoordenador || sindicatoDados.telefoneCordenador || "";
      const responsavel = sindicatoDados.responsavelPreenchimento || "";
      const cargo = sindicatoDados.cargoResponsavel || "";

      initialEditableRef.current = {
        telefone,
        coordenador,
        telCoordenador,
        responsavel,
        cargo,
      };

      setFormData({
        nome: sindicatoDados.nome || sindicatoDados.nomeSindicato || "",
        cnpj: sindicatoDados.cnpj || "",
        sigla: sindicatoDados.sigla || "",
        regional: sindicatoDados.regional || "",
        ordenacao: String(sindicatoDados.ordenacao ?? ""),
        grupo: sindicatoDados.grupo || "",
        presidente: sindicatoDados.nomePresidente || sindicatoDados.presidente || "",
        email: sindicatoDados.emailSindicato || "",
        telefone,
        coordenador,
        telCoordenador,
        responsavel,
        cargo,
      });

      setLoading(false);
    }, [isLoadingDados, sindicatoDados]);

    useEffect(() => {
      if (formData.responsavel) {
        localStorage.setItem("responsavelQuestionaro", formData.responsavel);
      }
      if (formData.cargo) {
        localStorage.setItem("cargoResponsavel", formData.cargo);
      }
    }, [formData.responsavel, formData.cargo]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const { name, value } = e.target;
      setFormData((prev) => ({ ...prev, [name]: value }));
      setErrors((prev) => ({ ...prev, [name]: false }));
    };

    const handleMaskedChange = (name: keyof FormState, value: string) => {
      setFormData((prev) => ({ ...prev, [name]: value }));
      setErrors((prev) => ({ ...prev, [name]: false }));
    };

    const buildPatchPayload = useCallback((): PatchAvaliacaoSolicitacaoPayload | null => {
      const initial = initialEditableRef.current;
      if (!initial) return null;

      const normalizeEmpty = (v: string | null | undefined) =>
        v == null ? "" : String(v).trim();

      const telefoneAtual = normalizeEmpty(formData.telefone);
      const coordenadorAtual = normalizeEmpty(formData.coordenador);
      const telCoordenadorAtual = normalizeEmpty(formData.telCoordenador);
      const responsavelAtual = normalizeEmpty(formData.responsavel);
      const cargoAtual = normalizeEmpty(formData.cargo);

      const telefoneInicial = normalizeEmpty(initial.telefone);
      const coordenadorInicial = normalizeEmpty(initial.coordenador);
      const telCoordenadorInicial = normalizeEmpty(initial.telCoordenador);
      const responsavelInicial = normalizeEmpty(initial.responsavel);
      const cargoInicial = normalizeEmpty(initial.cargo);

      const avaliacaoPatch: PatchAvaliacaoSolicitacaoPayload["avaliacao"] = {};
      let hasAvaliacaoPatch = false;

      if (responsavelInicial !== responsavelAtual) {
        avaliacaoPatch.responsavelPreenchimento = responsavelAtual;
        hasAvaliacaoPatch = true;
      }

      if (cargoInicial !== cargoAtual) {
        avaliacaoPatch.cargoResponsavel = cargoAtual;
        hasAvaliacaoPatch = true;
      }

      const sindicatoPatch: PatchAvaliacaoSolicitacaoPayload["sindicato"] = {};
      let hasSindicatoPatch = false;

      if (telefoneInicial !== telefoneAtual) {
        sindicatoPatch.telefoneSindicato = telefoneAtual;
        hasSindicatoPatch = true;
      }

      if (coordenadorInicial !== coordenadorAtual) {
        sindicatoPatch.nomeCoordenador = coordenadorAtual;
        hasSindicatoPatch = true;
      }

      if (telCoordenadorInicial !== telCoordenadorAtual) {
        sindicatoPatch.telefoneCoordenador = telCoordenadorAtual;
        hasSindicatoPatch = true;
      }

      if (!hasAvaliacaoPatch && !hasSindicatoPatch) {
        return null;
      }

      const payload: PatchAvaliacaoSolicitacaoPayload = {};
      if (hasAvaliacaoPatch) payload.avaliacao = avaliacaoPatch;
      if (hasSindicatoPatch) payload.sindicato = sindicatoPatch;
      return payload;
    }, [formData]);

    const markAsSaved = useCallback(() => {
      const normalizeEmpty = (v: string | null | undefined) =>
        v == null ? "" : String(v).trim();

      initialEditableRef.current = {
        telefone: normalizeEmpty(formData.telefone),
        coordenador: normalizeEmpty(formData.coordenador),
        telCoordenador: normalizeEmpty(formData.telCoordenador),
        responsavel: normalizeEmpty(formData.responsavel),
        cargo: normalizeEmpty(formData.cargo),
      };
    }, [formData]);

    const handleSave = useCallback(async () => {
      const newErrors = {
        responsavel: !formData.responsavel.trim(),
        cargo: !formData.cargo.trim(),
        telefone: !formData.telefone.trim(),
        coordenador: !formData.coordenador.trim(),
        telCoordenador: !formData.telCoordenador.trim(),
      };

      setErrors(newErrors);

      if (Object.values(newErrors).some(Boolean)) {
        return {
          show: true,
          message: "Preencha os campos obrigatórios.",
          type: "error",
        };
      }

      if (!solicitacaoId) {
        return {
          show: true,
          message: "Não foi possível salvar: solicitacaoId não informado.",
          type: "error",
        };
      }

      if (!initialEditableRef.current) {
        return {
          show: true,
          message: "Não foi possível salvar: dados iniciais não carregados.",
          type: "error",
        };
      }
      const payload = buildPatchPayload();
      if (!payload) {
        return {
          show: true,
          message: "Salvo com sucesso!",
          type: "success",
        };
      }

      try {
        await patchAvaliacaoSolicitacao(solicitacaoId, payload);
        markAsSaved();

        return {
          show: true,
          message: "Informações salvas com sucesso!",
          type: "success",
        };
      } catch {
        return {
          show: true,
          message: "Erro ao salvar informações.",
          type: "error",
        };
      }
    }, [formData, solicitacaoId, buildPatchPayload, markAsSaved]);

    const isDirty = useCallback(() => {
      return Boolean(buildPatchPayload());
    }, [buildPatchPayload]);

    const handleSubmit = async () => {
      const newErrors = {
        responsavel: !formData.responsavel.trim(),
        cargo: !formData.cargo.trim(),
        telefone: !formData.telefone.trim(),
        coordenador: !formData.coordenador.trim(),
        telCoordenador: !formData.telCoordenador.trim(),
      };

      setErrors(newErrors);

      if (Object.values(newErrors).some(Boolean)) {
        return {
          show: true,
          message: "Preencha os campos obrigatórios.",
          type: "error",
        };
      }

      if (onEnviarPreConfirmacao) {
        return onEnviarPreConfirmacao();
      }

      onProximo?.();
      // Sem toast/overlay: a troca de aba já dá feedback visual.
    };

    useImperativeHandle(
      ref,
      () => ({ handleSave, isDirty, buildPatchPayload, markAsSaved }),
      [handleSave, isDirty, buildPatchPayload, markAsSaved],
    );

    if (!isActive) return null;
    if (loading) return <PageLoading minHeight="50vh" />;

    const obrigatorioVazio = {
      telefone: !formData.telefone.trim(),
      coordenador: !formData.coordenador.trim(),
      telCoordenador: !formData.telCoordenador.trim(),
      responsavel: !formData.responsavel.trim(),
      cargo: !formData.cargo.trim(),
    };

    const destacarCampo = (key: keyof typeof obrigatorioVazio) =>
      Boolean(errors[key] || obrigatorioVazio[key]);

    return (
      <Container className="cadastro-container">
        <h1 className="cadastro-title">DADOS DO SINDICATO</h1>

        <Form>
          <Row className="gy-3">
            <Col xs={12}>
              <Form.Group controlId="nome">
                <Form.Label className="cadastro-label">NOME DO SINDICATO</Form.Label>
                <Form.Control type="text" value={formData.nome} disabled />
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group controlId="cnpj">
                <Form.Label className="cadastro-label">CNPJ DO SINDICATO</Form.Label>
                <Form.Control type="text" value={formData.cnpj} disabled />
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group controlId="sigla">
                <Form.Label className="cadastro-label">SIGLA</Form.Label>
                <Form.Control type="text" value={formData.sigla} disabled />
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group controlId="regional">
                <Form.Label className="cadastro-label">REGIONAL</Form.Label>
                <Form.Control type="text" value={formData.regional} disabled />
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group controlId="ordenacao">
                <Form.Label className="cadastro-label">ORDENAÇÃO</Form.Label>
                <Form.Control type="text" value={formData.ordenacao} disabled />
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group controlId="grupo">
                <Form.Label className="cadastro-label">GRUPO</Form.Label>
                <Form.Control type="text" value={formData.grupo} disabled />
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group controlId="presidente">
                <Form.Label className="cadastro-label">PRESIDENTE</Form.Label>
                <Form.Control type="text" value={formData.presidente} disabled />
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group controlId="email">
                <Form.Label className="cadastro-label">E-MAIL DO SINDICATO</Form.Label>
                <Form.Control type="email" value={formData.email} disabled />
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group controlId="telefone">
                <Form.Label className="cadastro-label">TELEFONE DO SINDICATO *</Form.Label>
                <IMaskInput
                  mask="(00) 0000 0000"
                  value={formData.telefone}
                  onAccept={(value) => handleMaskedChange("telefone", String(value))}
                  className={`form-control ${destacarCampo("telefone") ? "invalid-field" : ""}`}
                  disabled={readOnly}
                />
                {errors.telefone && <p className="error-message">*Preencher o campo</p>}
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group controlId="coordenador">
                <Form.Label className="cadastro-label">NOME COORDENADOR(A) *</Form.Label>
                <Form.Control
                  type="text"
                  name="coordenador"
                  value={formData.coordenador}
                  onChange={handleChange}
                  className={destacarCampo("coordenador") ? "invalid-field" : ""}
                  disabled={readOnly}
                />
                {errors.coordenador && <p className="error-message">*Preencher o campo</p>}
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group controlId="telCoordenador">
                <Form.Label className="cadastro-label">TELEFONE COORDENADOR(A) *</Form.Label>
                <IMaskInput
                  mask="(00) 00000 0000"
                  value={formData.telCoordenador}
                  onAccept={(value) =>
                    handleMaskedChange("telCoordenador", String(value))
                  }
                  className={`form-control ${destacarCampo("telCoordenador") ? "invalid-field" : ""}`}
                  disabled={readOnly}
                />
                {errors.telCoordenador && (
                  <p className="error-message">*Preencher o campo</p>
                )}
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group controlId="responsavel">
                <Form.Label className="cadastro-label">
                  RESPONSÁVEL PELO QUESTIONÁRIO *
                </Form.Label>
                <Form.Control
                  type="text"
                  name="responsavel"
                  value={formData.responsavel}
                  onChange={handleChange}
                  className={destacarCampo("responsavel") ? "invalid-field" : ""}
                  disabled={readOnly}
                />
                {errors.responsavel && <p className="error-message">*Campo Obrigatório</p>}
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group controlId="cargo">
                <Form.Label className="cadastro-label">CARGO NO SINDICATO *</Form.Label>
                <Form.Control
                  type="text"
                  name="cargo"
                  value={formData.cargo}
                  onChange={handleChange}
                  className={destacarCampo("cargo") ? "invalid-field" : ""}
                  disabled={readOnly}
                />
                {errors.cargo && <p className="error-message">*Campo Obrigatório</p>}
              </Form.Group>
            </Col>
          </Row>
        </Form>

        {!readOnly ? (
          <div className="buttons-container">
            <SaveSubmitButtons
              onSave={onSaveAll ?? handleSave}
              onSubmit={handleSubmit}
              submitLabel={submitLabel}
              submitDisabled={submitDisabled}
            />
          </div>
        ) : null}
      </Container>
    );
  }
);

CadastroTabV2.displayName = "CadastroTabV2";
export default CadastroTabV2;